/* eslint-disable prefer-template */
/* eslint-disable no-plusplus */
const rutLikePattern = () => /^(\d{0,2})\.?(\d{3})\.?(\d{3})-?(\d|K)$/gi;
const suspiciousRutPattern = () => /^(\d)\1?\.?(\1{3})\.?(\1{3})-?(\d|K)?$/gi;

export const isRutLike = (rut) => rutLikePattern().test(rut);
export const isSuspiciousRut = (rut) => suspiciousRutPattern().test(rut);

export const cleanRut = (rut) =>
  isRutLike(rut) ? rut.replace(/[^0-9K]/gi, "") : "";

export const getRutDigits = (rut) => cleanRut(rut).slice(0, -1);
export const getRutVerifier = (rut) => cleanRut(rut).slice(-1);

export const deconstructRut = (rut) => ({
  digits: getRutDigits(rut),
  verifier: getRutVerifier(rut),
});

export const RutFormat = {
  DOTS: "DOTS",
  DASH: "DASH",
  DOTS_DASH: "DOTS_DASH",
};

export const formatRut = (rut, format = RutFormat.DASH) => {
  /* istanbul ignore next */
  if (!isRutLike(rut)) return rut;

  switch (format) {
    case RutFormat.DOTS:
      // Sin template literals anidados
      return rut.replace(
        rutLikePattern(),
        (...args) => {
          const prefix = args[1] ? `${args[1]}.` : "";
          return `${prefix}${args[2]}.${args[3]}${args[4]}`;
        }
      );

    case RutFormat.DASH:
      return rut.replace(rutLikePattern(), "$1$2$3-$4");

    case RutFormat.DOTS_DASH:
      // Sin template literals anidados
      return rut.replace(
        rutLikePattern(),
        (...args) => {
          const prefix = args[1] ? `${args[1]}.` : "";
          return `${prefix}${args[2]}.${args[3]}-${args[4]}`;
        }
      );
    /* istanbul ignore next */
    default:
      return rut.replace(rutLikePattern(), "$1$2$3$4");
  }
};

/* istanbul ignore next */
export const calculateRutVerifier = (digits) => {
  let sum = 0;
  let mul = 2;
  let i = digits.length;
  while (i--) {
    // eslint-disable-next-line operator-assignment, radix
    sum = sum + parseInt(digits.charAt(i)) * mul;
    if (mul % 7 === 0) mul = 2;
    else mul += 1;
  }
  const res = sum % 11;
  if (res === 0) return "0";
  if (res === 1) return "k";
  return `${11 - res}`;
};

/* istanbul ignore next */
export const validateRut = (rut, noSuspicious = true) => {
  if (!isRutLike(rut)) return false;
  if (noSuspicious && isSuspiciousRut(rut)) return false;
  return (
    getRutVerifier(rut).toLowerCase() ===
    calculateRutVerifier(getRutDigits(rut))
  );
};

export const validateRutList = (ruts, noSuspicious = true) => {
  return ruts.reduce((result, rut) => {
    result.set(rut, validateRut(rut, noSuspicious));
    return result;
  }, new Map());
};

export const generateRut = () => {
  // Generar número aleatorio criptográficamente seguro
  const randomArray = new Uint32Array(1);
  crypto.getRandomValues(randomArray);

  // Rango: 10000003 - 99999999 (RUTs válidos en Chile)
  const min = 10000003;
  const max = 99999999;
  const range = max - min + 1;
  const randomNum = min + (randomArray[0] % range);

  const digits = randomNum.toString();
  const verifier = calculateRutVerifier(digits);
  return formatRut(digits + verifier);
};

/* istanbul ignore next */
export const rutEncryptedHash256 = async (rut) => {
  // encode as UTF-8
  const rutBuffer = new TextEncoder().encode(rut);
  // hash the rut
  const hashBuffer = await crypto.subtle.digest("SHA-256", rutBuffer);
  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // convert bytes to hex string
  const hashHex = hashArray
    .map((b) => ("00" + b.toString(16)).slice(-2))
    .join("");
  return hashHex;
};

/* istanbul ignore next */
export const formatNamePreserveAccents = (str) => {
  if (!str) return "";

  return str
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/* istanbul ignore next */
export const formatDateToLongSpanish = (dateStr) => {
  const [day, month, year] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  const options = { weekday: "long", day: "numeric", month: "long" };
  const formatted = date.toLocaleDateString("es-CL", options);

  // Capitaliza cada palabra del resultado (día y mes)
  const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

  return formatted.split(" ").map(capitalize).join(" ");
};